import { useState, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { TestAgentResult } from "@ai-crm/shared";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Send, Bot, User, Loader2 } from "lucide-react";

interface Message {
  role: "user" | "assistant";
  text: string;
  meta?: { model: string; tokensUsed: number };
}

interface TestAgentPanelProps {
  funnelId: string;
}

export function TestAgentPanel({ funnelId }: TestAgentPanelProps) {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  const test = useMutation({
    mutationFn: (message: string) => api.testAgent(funnelId, message),
    onMutate: (message) => {
      setMessages((prev) => [...prev, { role: "user", text: message }]);
    },
    onSuccess: (result: TestAgentResult) => {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: result.reply,
          meta: { model: result.model, tokensUsed: result.tokensUsed },
        },
      ]);
    },
    onError: (err: Error) => {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: `Ошибка: ${err.message}` },
      ]);
    },
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages]);

  function handleSend() {
    const trimmed = input.trim();
    if (!trimmed || test.isPending) return;
    setInput("");
    test.mutate(trimmed);
  }

  return (
    <div className="flex h-80 flex-col">
      <div
        ref={scrollRef}
        className="flex-1 space-y-3 overflow-y-auto p-3"
      >
        {messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center text-center text-muted-foreground">
            <Bot className="mb-2 h-8 w-8" />
            <p className="text-sm">Отправьте сообщение, чтобы протестировать агента</p>
          </div>
        )}

        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            {msg.role === "assistant" && (
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <Bot className="h-3.5 w-3.5 text-primary" />
              </div>
            )}
            <div
              className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                msg.role === "user"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted"
              }`}
            >
              <p className="whitespace-pre-wrap">{msg.text}</p>
              {msg.meta && (
                <p className="mt-1 text-xs opacity-60">
                  {msg.meta.model} · {msg.meta.tokensUsed} tokens
                </p>
              )}
            </div>
            {msg.role === "user" && (
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <User className="h-3.5 w-3.5 text-primary" />
              </div>
            )}
          </div>
        ))}

        {test.isPending && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <Bot className="h-3.5 w-3.5 text-primary" />
            </div>
            <div className="flex items-center gap-1.5 rounded-lg bg-muted px-3 py-2 text-sm">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Думаю...
            </div>
          </div>
        )}
      </div>

      <div className="border-t p-3">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
          className="flex gap-2"
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Введите сообщение..."
            disabled={test.isPending}
          />
          <Button
            type="submit"
            size="icon"
            disabled={!input.trim() || test.isPending}
          >
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
